import React, { useEffect, useState } from "react"
import { CardContentWrapper, CardWrapper } from "../../../../Cards/base"
import { FaTasks } from "react-icons/fa"
import ProgressCardHeading from "./ProgressCardHeading"
import { iGDM_CALC_STRUCTURE } from "../../Structure"
import DonutChart from "./DonutChart"
import SiteParagraph from "../../../../../utils/SiteParagraph"
import smoothscroll from "smoothscroll-polyfill"

export interface iProgressCardDataItem {
  value: any
  error: string | null
  warning: string | null
}

interface iProgressCard {
  data: {
    [id: string]: iProgressCardDataItem
  }
  calculatorStructure: iGDM_CALC_STRUCTURE
}

export interface iProgressStateDataItem {
  key: string
  title: string
  filled: boolean
}

const ProgressCardView: React.FC<{
  percentage_complete: number
  data: iProgressStateDataItem[]
  onCellClick: (dataKey: string) => void
}> = ({ percentage_complete, data, onCellClick }) => {
  return (
    <CardWrapper className="hover:shadow-none">
      <ProgressCardHeading
        percentage_complete={percentage_complete}
        Icon={<FaTasks />}
      />
      <CardContentWrapper>
        <div className="flex items-center justify-center">
          <DonutChart data={data} onCellClick={onCellClick} />
        </div>
        <SiteParagraph className="text-xs text-center mt-2 text-site-blue">
          Click on a cell to go to the respective field
        </SiteParagraph>
      </CardContentWrapper>
    </CardWrapper>
  )
}

const isDataItemFilled = ({ value, error, warning }: iProgressCardDataItem) => {
  if (value) {
    if (error) return false
    if (warning) return false
    return true
  }
  return false
}

const calculateFilledPercentage = (progressData: iProgressStateDataItem[]) => {
  if (progressData.length === 0) return 0
  let filledCount = 0
  progressData.forEach(item => {
    if (item.filled) filledCount += 1
  })
  return (filledCount / progressData.length) * 100
}

const ProgressCard: React.FC<iProgressCard> = ({
  data,
  calculatorStructure,
}) => {
  const [progressData, setProgressData] = useState<iProgressStateDataItem[]>([])

  useEffect(() => {
    const _progressData = Object.keys(calculatorStructure).map(
      (key: string) => ({
        key,
        title: calculatorStructure[key].label,
        filled: isDataItemFilled(data[key]),
      })
    )
    setProgressData(_progressData)
    smoothscroll.polyfill()
  }, [])

  useEffect(() => {
    if (progressData.length === 0) return
    const _progressData = progressData.map((item: iProgressStateDataItem) => ({
      ...item,
      filled: isDataItemFilled(data[item.key]),
    }))
    setProgressData(_progressData)
  }, [data])

  const onCellClick = (dataKey: string) => {
    if (!dataKey) return
    if (!document) return

    const element = document.getElementById(dataKey) // The respective ids are already assigned
    // to the form input components
    if (!element) return
    const halfInnerHeight = window.innerHeight >> 1
    const elementRect = element.getBoundingClientRect()
    window.scrollBy({
      top: elementRect.top - halfInnerHeight,
      left: 0,
      behavior: "smooth",
    })
  }

  return (
    <ProgressCardView
      percentage_complete={calculateFilledPercentage(progressData)}
      data={progressData}
      onCellClick={onCellClick}
    />
  )
}

export default ProgressCard
